import { useCallback, useEffect, useRef, useState } from "react";

import { InteractionManager } from "react-native";

import useAppForegroundState from "@/hooks/useAppForegroundState";
import { isAndroid, isWeb } from "@/utils/platform.util";

interface SplashScreenControllerDeps {
  interactionManager?: typeof InteractionManager;
  timerDelay?: number;
}

const useSplashScreenController = ({
  interactionManager = InteractionManager,
  timerDelay = 50,
}: SplashScreenControllerDeps = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isEnabled, setIsEnabled] = useState(true);

  // enable/disable splash screen, in cases where we don't want to show the splash screen
  // e.g when the app is in active because of image picker or notification permission
  const enable = () => {
    // Android cannot use this implementation as it breaks native flows, both for OS level
    // actions (image/document pickers) as well as general background states
    if (isAndroid()) return;
    setIsEnabled(true);
  };
  const disable = () => setIsEnabled(false);

  const handleChange = useCallback(
    async ({ nextState }: { nextState: string }) => {
      if (isAndroid()) return;
      if (isWeb()) return;

      if (nextState !== "active" && isEnabled) {
        await interactionManager.runAfterInteractions(() => {
          setTimeout(() => setIsVisible(true), timerDelay);
        });
        return;
      }
      setIsVisible(false);
    },
    [isEnabled, interactionManager, timerDelay],
  );

  const ref = useRef(handleChange);
  useEffect(() => {
    ref.current = handleChange;
  }, [handleChange]);

  useAppForegroundState({ onChangeRef: ref });

  return { disable, enable, isVisible };
};

type UseSplashScreenController = ReturnType<typeof useSplashScreenController>;

export type { UseSplashScreenController };
export default useSplashScreenController;
